import { observer } from 'mobx-react';
import React, { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, Grid } from '@mui/material';
import Modal, { IModalProps } from 'components/Modal';
import GrowTransition from 'components/Modal/animations/grow';
import { Form, Formik, FormikHelpers } from 'formik';
import { getSnapshot } from 'mobx-state-tree';
import { thanksModalConfig } from 'modules/Programs/Program/ThanksModal/constants';

import image from '@common/assets/images/thankYouModalImage.png';
import TextField from '@common/components/Form/TextField';
import { IStudentModel } from '@common/models/Users/Students/Student';

import * as Styled from './styled';

interface ThanksModalProps extends Pick<IModalProps, 'onClose'> {
	variant?: 'moreLessons' | 'complete';
	open?: boolean;
	student?: IStudentModel;
}

function ThanksModal({
	variant = 'moreLessons',
	onClose,
	open = false,
	student,
	...props
}: ThanksModalProps) {
	const { t } = useTranslation();
	const { title, subtitle } = thanksModalConfig[variant];

	const handleChangePhone = useCallback(
		(
			e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
			setFieldValue: FormikHelpers<{ phone: string }>['setFieldValue'],
		) => {
			const phoneValue = e.target.value;
			const formattedPhoneValue = phoneValue.replace(/[^\d]/g, '');
			const internationalPhoneValue = formattedPhoneValue ? `+${formattedPhoneValue}` : '';
			setFieldValue('phone', internationalPhoneValue);
		},
		[],
	);

	const handleSubmit = async (data: { phone: string }) => {
		try {
			await student.save({ ...getSnapshot(student), phone: data.phone });
			onClose?.();
		} catch (error) {
			console.error('Error', error);
		}
	};

	return (
		<Modal
			PaperProps={{
				sx: {
					maxWidth: '424px',
				},
			}}
			open={open}
			onClose={onClose}
			image={image}
			transitionDuration={500}
			TransitionComponent={GrowTransition}
			{...props}
		>
			<Formik
				onSubmit={handleSubmit}
				initialValues={{
					phone: '',
				}}
			>
				{({ setFieldValue }) => (
					<Form>
						<Grid container gap={2} direction="column">
							<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 1 }}>
								<Styled.Title>{title}</Styled.Title>
								<Styled.Subtitle>{subtitle}</Styled.Subtitle>
								<TextField
									label={t('shared.phone')}
									name="phone"
									placeholder={t('shared.phone')}
									onChange={(e) => handleChangePhone(e, setFieldValue)}
								/>
							</Box>
							<Button type="submit" variant="contained" fullWidth sx={{ borderRadius: '10px' }}>
								{t('shared.send')}
							</Button>
						</Grid>
					</Form>
				)}
			</Formik>
		</Modal>
	);
}

export default observer(ThanksModal);
